import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiPost } from '../services/api';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatResponse {
  success: boolean;
  data: { reply: string };
}

const WELCOME_MESSAGE =
  'Hei! Jeg er InntektsAssistenten for Kristiansund kommune. Du kan spørre meg om budsjett, avtaler og avtaledokumenter. Hva lurer du på?';

export function ChatPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'assistant', content: WELCOME_MESSAGE },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = async () => {
    const message = input.trim();
    if (!message || loading) return;

    const userMessage: ChatMessage = { role: 'user', content: message };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setError(null);
    setLoading(true);

    try {
      // Pass history (excluding welcome message) to API
      const historyToSend = updatedMessages
        .slice(1) // skip static welcome
        .slice(-10); // limit to last 10

      const response = await apiPost<ChatResponse>('/chat', {
        message,
        history: historyToSend.slice(0, -1), // history before this new message
      });

      setMessages([
        ...updatedMessages,
        { role: 'assistant', content: response.data.reply },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Noe gikk galt. Prøv igjen.');
    } finally {
      setLoading(false);
      // Return focus to input after response
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 160px)',
        maxWidth: 900,
        mx: 'auto',
      }}
    >
      <Typography variant="h1" gutterBottom>
        Inntektsassistent
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Still spørsmål om budsjett, avtaler og avtaledokumenter. Assistenten har tilgang til
        kommunens inntektsdata og kan «chatte med dokumentene».
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {/* Message list */}
      <Paper
        variant="outlined"
        sx={{ flex: 1, overflow: 'auto', p: 2, mb: 2, bgcolor: 'grey.50' }}
        aria-live="polite"
        aria-label="Samtalehistorikk"
        role="log"
      >
        {messages.map((msg, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              alignItems: 'flex-start',
              mb: 2,
            }}
          >
            {msg.role === 'assistant' && (
              <SmartToyIcon
                sx={{ mr: 1, mt: 0.5, color: 'primary.main', flexShrink: 0 }}
                aria-hidden="true"
              />
            )}

            <Paper
              elevation={msg.role === 'user' ? 2 : 1}
              sx={{
                p: 1.5,
                maxWidth: '75%',
                bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                borderRadius:
                  msg.role === 'user'
                    ? '16px 16px 4px 16px'
                    : '4px 16px 16px 16px',
              }}
            >
              {msg.role === 'user' ? (
                <Typography
                  variant="body1"
                  component="p"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}
                >
                  {msg.content}
                </Typography>
              ) : (
                <Box
                  sx={{
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                    '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
                    '& h1, & h2, & h3, & h4': { mt: 1.5, mb: 0.5, lineHeight: 1.3 },
                    '& h1': { fontSize: '1.2rem', fontWeight: 700 },
                    '& h2': { fontSize: '1.1rem', fontWeight: 700 },
                    '& h3': { fontSize: '1rem', fontWeight: 600 },
                    '& ul, & ol': { pl: 2.5, my: 0.5 },
                    '& li': { mb: 0.25 },
                    '& table': {
                      borderCollapse: 'collapse',
                      width: '100%',
                      my: 1,
                      fontSize: '0.875rem',
                    },
                    '& th, & td': {
                      border: '1px solid',
                      borderColor: 'divider',
                      px: 1,
                      py: 0.5,
                      textAlign: 'left',
                    },
                    '& th': { fontWeight: 600, bgcolor: 'action.hover' },
                    '& code': {
                      fontFamily: 'monospace',
                      fontSize: '0.85em',
                      bgcolor: 'action.hover',
                      px: 0.5,
                      borderRadius: 0.5,
                    },
                    '& pre': {
                      bgcolor: 'grey.900',
                      color: 'grey.100',
                      p: 1.5,
                      borderRadius: 1,
                      overflow: 'auto',
                      my: 1,
                      '& code': { bgcolor: 'transparent', p: 0 },
                    },
                    '& blockquote': {
                      borderLeft: 3,
                      borderColor: 'primary.main',
                      pl: 1.5,
                      ml: 0,
                      my: 1,
                      color: 'text.secondary',
                    },
                    '& strong': { fontWeight: 600 },
                    '& hr': { border: 'none', borderTop: 1, borderColor: 'divider', my: 1.5 },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                </Box>
              )}
            </Paper>

            {msg.role === 'user' && (
              <PersonIcon
                sx={{ ml: 1, mt: 0.5, color: 'text.secondary', flexShrink: 0 }}
                aria-hidden="true"
              />
            )}
          </Box>
        ))}

        {loading && (
          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}
            role="status"
            aria-label="Henter svar fra assistent"
          >
            <SmartToyIcon sx={{ mr: 1, mt: 0.5, color: 'primary.main' }} aria-hidden="true" />
            <Paper
              elevation={1}
              sx={{ p: 1.5, borderRadius: '4px 16px 16px 16px', display: 'flex', gap: 1, alignItems: 'center' }}
            >
              <CircularProgress size={16} thickness={5} />
              <Typography variant="body2" color="text.secondary">
                Tenker...
              </Typography>
            </Paper>
          </Box>
        )}

        <div ref={messagesEndRef} aria-hidden="true" />
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {/* Input area */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Skriv et spørsmål… (Enter for å sende, Shift+Enter for ny linje)"
          disabled={loading}
          size="small"
          inputProps={{
            'aria-label': 'Skriv melding til assistenten',
            maxLength: 2000,
          }}
        />
        <IconButton
          onClick={sendMessage}
          disabled={!input.trim() || loading}
          color="primary"
          aria-label="Send melding"
          sx={{
            border: 1,
            borderColor: 'primary.main',
            borderRadius: 1,
            '&:disabled': { borderColor: 'action.disabled' },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'right' }}>
        {input.length}/2000
      </Typography>
    </Box>
  );
}
